import React, { useState, useCallback, useEffect } from 'react';
import { Assembly, AssemblyMinutesStatus, AssemblyStatus } from '../../types/assembly';
import { assemblyService } from '../../services/assemblyService';
import AssemblySelector from './AssemblySelector';
import AssemblyForm from './AssemblyForm';
import { useAuth } from '../../context/AuthContext';
import { useConfirm } from '../confirm/ConfirmDialogProvider';
import { notifyError, notifySuccess, notifyWarning, notifyInfo } from '../../utils/notificationCenter';
import { getAssemblyStatusLabel, getAssemblyStatusBadgeClass } from '../../utils/assemblyLabels';
import { formatAssemblyCalendarDateEs, formatAssemblyTimeEs } from '../../utils/assemblyDate';
import { 
  PlusIcon,
  PencilIcon,
  TrashIcon,
  PlayIcon,
  StopIcon,
  ArrowPathIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  EnvelopeIcon
} from '@heroicons/react/24/outline';

interface AssemblyManagerProps {
  onAssemblySelect: (assembly: Assembly | null) => void;
  selectedAssembly?: Assembly | null;
  showHeader?: boolean;
  showSelectorOnly?: boolean;
}

const AssemblyManager: React.FC<AssemblyManagerProps> = ({
  onAssemblySelect,
  selectedAssembly,
  showHeader = true,
  showSelectorOnly = false
}) => {
  const { user } = useAuth();
  const [showForm, setShowForm] = useState(false);
  const [editingAssembly, setEditingAssembly] = useState<Assembly | undefined>();
  const [loading, setLoading] = useState(false);
  const [selectorKey, setSelectorKey] = useState(0); // Para forzar re-render del selector
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [rescheduleTarget, setRescheduleTarget] = useState<Assembly | null>(null);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleTime, setRescheduleTime] = useState('');
  const [rescheduleReason, setRescheduleReason] = useState('');
  const [minutesStatus, setMinutesStatus] = useState<AssemblyMinutesStatus | null>(null);
  const [isLoadingMinutesStatus, setIsLoadingMinutesStatus] = useState(false);

  const isAdmin = user?.role === 'admin';
  const canManageAssemblies = user?.role === 'admin' || user?.role === 'manager';
  const { confirm } = useConfirm();

  // Refrescar selector
  const refreshSelector = useCallback(() => {
    setSelectorKey(prev => prev + 1);
  }, []);

  // Manejar selección de asamblea
  const handleAssemblySelect = (assembly: Assembly) => {
    onAssemblySelect(assembly);
  };

  const refreshMinutesStatus = useCallback(async (assemblyId: string) => {
    setIsLoadingMinutesStatus(true);
    try {
      const status = await assemblyService.getMinutesStatus(assemblyId);
      setMinutesStatus(status);
    } catch (error) {
      console.error('Error al cargar estado del acta:', error);
      setMinutesStatus(null);
    } finally {
      setIsLoadingMinutesStatus(false);
    }
  }, []);

  useEffect(() => {
    if (!selectedAssembly || selectedAssembly.status !== AssemblyStatus.COMPLETED) {
      setMinutesStatus(null);
      return;
    }

    void refreshMinutesStatus(selectedAssembly.id);

    if (minutesStatus?.pdfReady || minutesStatus?.jobStatus === 'failed') {
      return;
    }

    const intervalId = window.setInterval(() => {
      void refreshMinutesStatus(selectedAssembly.id);
    }, 10000);

    return () => window.clearInterval(intervalId);
  }, [selectedAssembly, refreshMinutesStatus, minutesStatus?.pdfReady, minutesStatus?.jobStatus]);

  const getMinutesStatusLabel = () => {
    if (!selectedAssembly || selectedAssembly.status !== AssemblyStatus.COMPLETED) {
      return null;
    }

    if (isLoadingMinutesStatus && !minutesStatus) {
      return {
        label: 'Verificando acta...',
        className: 'border-slate-200 bg-slate-50 text-slate-700',
      };
    }

    if (minutesStatus?.pdfReady) {
      return {
        label: 'Acta lista para descargar',
        className: 'border-emerald-200 bg-emerald-50 text-emerald-700',
      };
    }

    if (minutesStatus?.jobStatus === 'failed') {
      return {
        label: 'La generación falló',
        className: 'border-red-200 bg-red-50 text-red-700',
      };
    }

    if (minutesStatus?.jobStatus === 'processing' || minutesStatus?.jobStatus === 'pending') {
      return {
        label: 'Acta en generación',
        className: 'border-amber-200 bg-amber-50 text-amber-700',
      };
    }

    return {
      label: 'Acta pendiente de preparación',
      className: 'border-slate-200 bg-slate-50 text-slate-700',
    };
  };

  // Abrir formulario para crear nueva asamblea
  const handleCreateNew = () => {
    setEditingAssembly(undefined);
    setShowForm(true);
  };

  // Abrir formulario para editar asamblea
  const handleEdit = (assembly: Assembly) => {
    setEditingAssembly(assembly);
    setShowForm(true);
  };

  // Manejar guardado (crear/editar)
  const handleSave = (assembly: Assembly) => {
    // Actualizar editingAssembly con la versión guardada
    // Esto asegura que próximas ediciones tengan los datos correctos
    setEditingAssembly(assembly);
    
    // Refrescar lista y notificar al padre
    refreshSelector();
    onAssemblySelect(assembly);
    
    // Cerrar modal después de un pequeño delay para que se vea la actualización
    setTimeout(() => {
      setShowForm(false);
      setEditingAssembly(undefined);
    }, 300);
  };

  // Eliminar asamblea
  const handleDelete = async (assembly: Assembly) => {
    const shouldDelete = await confirm({
      title: 'Eliminar asamblea',
      message: `¿Estás seguro de que deseas eliminar la asamblea "${assembly.title}"? Esta acción no se puede deshacer.`,
      confirmText: 'Eliminar',
      cancelText: 'Cancelar',
      variant: 'danger',
    });

    if (!shouldDelete) {
      return;
    }

    try {
      setLoading(true);
      await assemblyService.deleteAssembly(assembly.id);
      refreshSelector();
      
      // Si la asamblea eliminada era la seleccionada, deseleccionar
      if (selectedAssembly?.id === assembly.id) {
        onAssemblySelect(null);
      }
      notifySuccess('Asamblea eliminada correctamente');
    } catch (err) {
      notifyError(err instanceof Error ? err.message : 'Error al eliminar la asamblea');
    } finally {
      setLoading(false);
    }
  };

  // Iniciar asamblea
  const handleStart = async (assembly: Assembly) => {
    const shouldStart = await confirm({
      title: 'Iniciar asamblea',
      message: `¿Estás seguro de que deseas iniciar la asamblea "${assembly.title}"? Esta acción no se puede revertir.`,
      confirmText: 'Iniciar',
      cancelText: 'Cancelar',
      variant: 'info',
    });

    if (!shouldStart) {
      return;
    }

    try {
      setLoading(true);
      const updatedAssembly = await assemblyService.startAssembly(assembly.id);
      refreshSelector();
      onAssemblySelect(updatedAssembly);
      notifySuccess('Asamblea iniciada correctamente');
    } catch (err) {
      notifyError(err instanceof Error ? err.message : 'Error al iniciar la asamblea');
    } finally {
      setLoading(false);
    }
  };

  // Completar asamblea
  const handleComplete = async (assembly: Assembly) => {
    const shouldComplete = await confirm({
      title: 'Completar asamblea',
      message: '¿Estás seguro de que deseas marcar esta asamblea como completada?',
      confirmText: 'Completar',
      cancelText: 'Cancelar',
      variant: 'warning',
    });

    if (!shouldComplete) {
      return;
    }

    try {
      setLoading(true);
      const updatedAssembly = await assemblyService.completeAssembly(assembly.id);
      refreshSelector();
      onAssemblySelect(updatedAssembly);
      notifySuccess('Asamblea completada correctamente');
    } catch (err) {
      notifyError(err instanceof Error ? err.message : 'Error al completar la asamblea');
    } finally {
      setLoading(false);
    }
  };

  // Reenviar invitaciones de asamblea
  const handleResendInvitations = async (assembly: Assembly) => {
    const shouldResend = await confirm({
      title: 'Reenviar invitaciones',
      message: 'Esto reenviará invitaciones a usuarios sin asistencia registrada. ¿Deseas continuar?',
      confirmText: 'Reenviar',
      cancelText: 'Cancelar',
      variant: 'info',
    });

    if (!shouldResend) {
      return;
    }

    try {
      setLoading(true);
      await assemblyService.resendInvitations(assembly.id);
      notifySuccess('Reenvío de invitaciones iniciado en segundo plano');
    } catch (err) {
      notifyError(err instanceof Error ? err.message : 'Error al reenviar invitaciones');
    } finally {
      setLoading(false);
    }
  };

  // Enviar recordatorios de asamblea
  const handleSendReminders = async (assembly: Assembly) => {
    const shouldSend = await confirm({
      title: 'Enviar recordatorio',
      message:
        'Se enviará un correo de recordatorio a los propietarios de los conjuntos de esta asamblea que aún no tienen asistencia registrada. ¿Deseas continuar?',
      confirmText: 'Enviar recordatorio',
      cancelText: 'Cancelar',
      variant: 'info',
    });

    if (!shouldSend) {
      return;
    }

    try {
      setLoading(true);
      await assemblyService.sendReminders(assembly.id);
      notifySuccess('Envío de recordatorios iniciado en segundo plano');
    } catch (err) {
      notifyError(err instanceof Error ? err.message : 'Error al enviar recordatorios');
    } finally {
      setLoading(false);
    }
  };

  // Descargar acta en PDF
const handleDownloadMinutes = async (assembly: Assembly) => {
    try {
      setLoading(true);
      const blob = await assemblyService.downloadMinutes(assembly.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const safeName = (assembly.title || 'Acta').replace(/[^a-zA-Z0-9]/g, '_').substring(0, 50);
      link.download = `Acta - ${safeName}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      notifySuccess('Acta descargada correctamente');
    } catch (err) {
      notifyError(err instanceof Error ? err.message : 'Error al descargar el acta');
    } finally {
      setLoading(false);
    }
  };

const handleDownloadAttendeesExcel = async (assembly: Assembly) => {
    try {
      setLoading(true);
      const blob = await assemblyService.downloadMinutesAttendeesExcel(assembly.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const safeName = (assembly.title || 'Asambla').replace(/[^a-zA-Z0-9]/g, '_').substring(0, 50);
      link.download = `Asistentes - ${safeName}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      notifySuccess('Lista de asistentes descargada correctamente');
    } catch (err) {
      notifyError(err instanceof Error ? err.message : 'Error al descargar asistentes');
    } finally {
      setLoading(false);
    }
  };

  const handleQueueMinutesGeneration = async (assembly: Assembly) => {
    try {
      setLoading(true);
      await assemblyService.queueMinutesGeneration(assembly.id);
      notifySuccess('Preparación de acta encolada. En unos segundos podrás descargarla.');
      await refreshMinutesStatus(assembly.id);
    } catch (err) {
      notifyError(err instanceof Error ? err.message : 'Error al preparar el acta');
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerateMinutes = async (assembly: Assembly) => {
    try {
      setLoading(true);
      notifyInfo('Regenerando acta... Esto puede tomar unos segundos.');
      await assemblyService.regenerateMinutes(assembly.id);
      notifySuccess('Acta regeneratea correctamente.');
      await refreshMinutesStatus(assembly.id);
    } catch (err) {
      notifyError(err instanceof Error ? err.message : 'Error al regenerar el acta');
    } finally {
      setLoading(false);
    }
  };

  const openRescheduleModal = (assembly: Assembly) => {
    setRescheduleTarget(assembly);
    setRescheduleDate(assembly.scheduledDate || '');
    setRescheduleTime((assembly.startTime || '09:00').slice(0, 5));
    setRescheduleReason('');
    setShowRescheduleModal(true);
  };

  const closeRescheduleModal = () => {
    if (loading) return;
    setShowRescheduleModal(false);
    setRescheduleTarget(null);
    setRescheduleDate('');
    setRescheduleTime('');
    setRescheduleReason('');
  };

  const handleReschedule = async () => {
    if (!rescheduleTarget) return;

    if (!rescheduleDate || !rescheduleTime) {
      notifyWarning('Debes indicar la nueva fecha y hora para reprogramar la asamblea.');
      return;
    }

    const selectedDateTime = new Date(`${rescheduleDate}T${rescheduleTime}`);
    if (Number.isNaN(selectedDateTime.getTime()) || selectedDateTime <= new Date()) {
      notifyWarning('La fecha y hora de reprogramación deben ser futuras.');
      return;
    }

    try {
      setLoading(true);
      const updatedAssembly = await assemblyService.rescheduleAssembly(rescheduleTarget.id, {
        scheduledDate: rescheduleDate,
        startTime: rescheduleTime,
        reason: rescheduleReason.trim() || undefined,
      });

      refreshSelector();
      onAssemblySelect(updatedAssembly);
      notifySuccess('Asamblea reprogramada correctamente.');
      closeRescheduleModal();
    } catch (err) {
      notifyError(err instanceof Error ? err.message : 'Error al reprogramar la asamblea');
    } finally {
      setLoading(false);
    }
  };

  // Cerrar formulario
  const handleCloseForm = () => {
    setShowForm(false);
    setEditingAssembly(undefined);
  };

  return (
    <div className="space-y-6">
      {showHeader && (
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Gestión de Asambleas
            </h1>
            <p className="text-gray-600 mt-2">
              Selecciona una asamblea existente o crea una nueva
            </p>
          </div>
          
          {canManageAssemblies && (
            <button
              onClick={handleCreateNew}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Nueva Asamblea
            </button>
          )}
        </div>
      )}

      {/* Assembly Selector */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6">
          <AssemblySelector
            key={selectorKey}
            onAssemblySelect={handleAssemblySelect}
            selectedAssemblyId={selectedAssembly?.id}
            showFilters={!showSelectorOnly}
          />
        </div>
      </div>

      {/* Assembly Actions */}
      {selectedAssembly && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900">
                  Asamblea Seleccionada
                </h3>
                <p className="text-sm text-gray-600">
                  Acciones disponibles para esta asamblea
                </p>
              </div>
            </div>

            {/* Assembly Details Card */}
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <h4 className="font-medium text-gray-900 mb-2">
                {selectedAssembly.title}
              </h4>
              {selectedAssembly.description && (
                <p className="text-sm text-gray-600 mb-2">
                  {selectedAssembly.description}
                </p>
              )}
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">
                  {formatAssemblyCalendarDateEs(selectedAssembly.scheduledDate, selectedAssembly.scheduledDate)}
                  {' - '}
                  {formatAssemblyTimeEs(selectedAssembly.startTime, 'Por confirmar')}
                </span>
                <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getAssemblyStatusBadgeClass(selectedAssembly.status)}`}>
                  {getAssemblyStatusLabel(selectedAssembly.status)}
                </span>
              </div>
              {selectedAssembly.status === AssemblyStatus.COMPLETED && getMinutesStatusLabel() && (
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getMinutesStatusLabel()?.className}`}>
                    {getMinutesStatusLabel()?.label}
                  </span>
                  {minutesStatus?.jobErrorMessage && (
                    <span className="text-xs text-gray-500">
                      {minutesStatus.jobErrorMessage}
                    </span>
                  )}
                </div>
              )}
              {/* Mostrar código de acceso para admin/manager */}
              {(isAdmin || canManageAssemblies) && (
                <div className="mt-3">
                  <span className="text-xs text-gray-500">Código de acceso:</span>
                  <span className="ml-2 font-mono text-base bg-gray-200 px-2 py-1 rounded uppercase">{selectedAssembly.code?.toUpperCase()}</span>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-2">
              {/* Ver Dashboard - disponible para todos */}
              <button
                onClick={() => {/* Esta acción se manejará en el componente padre */}}
                className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <EyeIcon className="h-4 w-4 mr-1" />
                Ver Dashboard
              </button>

              {/* Acciones de administración */}
              {canManageAssemblies && (
                <>
                  {/* Editar */}
                  <button
                    onClick={() => handleEdit(selectedAssembly)}
                    disabled={loading}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    <PencilIcon className="h-4 w-4 mr-1" />
                    Editar
                  </button>

                  {/* Iniciar (solo si está programada) */}
                  {selectedAssembly.status === AssemblyStatus.SCHEDULED && (
                    <button
                      onClick={() => handleStart(selectedAssembly)}
                      disabled={loading}
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50"
                    >
                      <PlayIcon className="h-4 w-4 mr-1" />
                      Iniciar Asamblea
                    </button>
                  )}

                  {/* Reenviar invitaciones (programada o en progreso) */}
                  {(selectedAssembly.status === AssemblyStatus.SCHEDULED ||
                    selectedAssembly.status === AssemblyStatus.IN_PROGRESS) && (
                    <button
                      onClick={() => handleResendInvitations(selectedAssembly)}
                      disabled={loading}
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-amber-600 hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-500 disabled:opacity-50"
                    >
                      <EnvelopeIcon className="h-4 w-4 mr-1" />
                      Reenviar Invitaciones
                    </button>
                  )}

                  {/* Recordatorio (programada o en progreso) */}
                  {(selectedAssembly.status === AssemblyStatus.SCHEDULED ||
                    selectedAssembly.status === AssemblyStatus.IN_PROGRESS) && (
                    <button
                      onClick={() => handleSendReminders(selectedAssembly)}
                      disabled={loading}
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
                    >
                      <EnvelopeIcon className="h-4 w-4 mr-1" />
                      Enviar recordatorio
                    </button>
                  )}

                  {/* Completar (solo si está en progreso) */}
                  {selectedAssembly.status === AssemblyStatus.IN_PROGRESS && (
                    <button
                      onClick={() => handleComplete(selectedAssembly)}
                      disabled={loading}
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                    >
                      <StopIcon className="h-4 w-4 mr-1" />
                      Completar Asamblea
                    </button>
                  )}

                  {/* Descargar Acta (solo si está completada) */}
                  {selectedAssembly.status === AssemblyStatus.COMPLETED && (
                    <button
                      onClick={() => openRescheduleModal(selectedAssembly)}
                      disabled={loading}
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-amber-600 hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-500 disabled:opacity-50"
                    >
                      <ArrowPathIcon className="h-4 w-4 mr-1" />
                      Reprogramar Asamblea
                    </button>
                  )}

                  {/* Descargar Acta (solo si está completada) */}
                  {selectedAssembly.status === AssemblyStatus.COMPLETED && (
                    <button
                      onClick={() => handleDownloadMinutes(selectedAssembly)}
                      disabled={loading || !minutesStatus?.pdfReady}
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
                    >
                      <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                      {minutesStatus?.pdfReady ? 'Descargar Acta PDF' : 'Acta en generación'}
                    </button>
                  )}

                  {selectedAssembly.status === AssemblyStatus.COMPLETED && (
                    <button
                      onClick={() => handleRegenerateMinutes(selectedAssembly)}
                      disabled={loading}
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-sky-600 hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-500 disabled:opacity-50"
                    >
                      <ArrowPathIcon className="h-4 w-4 mr-1" />
                      {minutesStatus?.jobStatus === 'processing' && loading
                        ? 'Regenerando...'
                        : minutesStatus?.pdfReady
                          ? 'Regenerar Acta'
                          : minutesStatus?.jobStatus === 'failed'
                            ? 'Reintentar'
                            : 'Preparar Acta'}
                    </button>
                  )}

                  {selectedAssembly.status === AssemblyStatus.COMPLETED && (
                    <button
                      onClick={() => handleDownloadAttendeesExcel(selectedAssembly)}
                      disabled={loading}
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-50"
                    >
                      <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                      Descargar Asistentes Excel
                    </button>
                  )}

                  {/* Eliminar (solo administradores y si no está en progreso) */}
                  {isAdmin && selectedAssembly.status !== AssemblyStatus.IN_PROGRESS && (
                    <button
                      onClick={() => handleDelete(selectedAssembly)}
                      disabled={loading}
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50"
                    >
                      <TrashIcon className="h-4 w-4 mr-1" />
                      Eliminar
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {showRescheduleModal && rescheduleTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 px-4 !mt-0">
          <div className="bg-white rounded-lg shadow-lg border border-gray-200 w-full max-w-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Reprogramar Asamblea</h3>
              <p className="mt-1 text-sm text-gray-600">
                Esta acción mueve la asamblea a estado programada y conserva delegaciones existentes.
              </p>
            </div>

            <div className="px-6 py-4 space-y-4">
              <div className="text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-md p-3">
                <span className="font-medium">Asamblea:</span> {rescheduleTarget.title}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nueva fecha</label>
                  <input
                    type="date"
                    value={rescheduleDate}
                    onChange={(e) => setRescheduleDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nueva hora</label>
                  <input
                    type="time"
                    value={rescheduleTime}
                    onChange={(e) => setRescheduleTime(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Motivo (opcional)</label>
                <textarea
                  value={rescheduleReason}
                  onChange={(e) => setRescheduleReason(e.target.value)}
                  rows={3}
                  placeholder="Ejemplo: No se alcanzó quórum y se reprograma para nueva convocatoria"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
              <button
                onClick={closeRescheduleModal}
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleReschedule}
                disabled={loading}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-amber-600 border border-transparent rounded-md hover:bg-amber-700 disabled:opacity-50"
              >
                <ArrowPathIcon className="h-4 w-4 mr-2" />
                Reprogramar Asamblea
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assembly Form Modal */}
      <AssemblyForm
        assembly={editingAssembly}
        isOpen={showForm}
        onClose={handleCloseForm}
        onSave={handleSave}
        onError={notifyError}
      />
    </div>
  );
};

export default AssemblyManager;